import { jsx as _jsx } from "preact/jsx-runtime";
import { createContext } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { useLocation } from 'preact-iso';
import { userApi } from '@/api/user';
import { apiClient } from '@/api/client';
import { signalRService } from '@/services/signalRService';
const AuthContext = createContext(null);
export { AuthContext };
export const AuthProvider = ({ children }) => {
    const [auth0Client, setAuth0Client] = useState(null);
    const [authState, setAuthState] = useState({
        isLoading: true,
        isAuthenticated: false,
        canManageCustomers: false,
        isParentCompany: false,
    });
    const location = useLocation();
    // Helper function to get ClientCompanyId from user
    const getClientCompanyId = (user) => {
        if (!user)
            return undefined;
        const claim = user['https://orbitalx.com/clientCompanyId'];
        if (typeof claim === 'string') {
            return claim.replace(/['"]/g, '');
        }
        return undefined;
    };
    // Consolidated function to fetch user profile and handle routing
    const fetchUserProfileAndRoute = async (clientCompanyId, user, shouldRoute = false) => {
        try {
            // If we need to route, do it immediately with the JWT company ID before fetching profile
            if (shouldRoute && clientCompanyId) {
                // Route to company bootstrap which will select first workspace
                location.route(`/company/${clientCompanyId}`);
            }
            const profileResponse = await userApi.getMe();
            if (profileResponse.data && !profileResponse.error) {
                // Set the auth state with profile data
                setAuthState(prev => ({
                    ...prev,
                    isLoading: false,
                    isAuthenticated: true,
                    user: user, // Make sure to include the Auth0 user object
                    userProfile: profileResponse.data.UserProfile,
                    homeClientCompanyId: profileResponse.data.HomeClientCompanyId,
                    userCompanyRoleSets: profileResponse.data.UserCompanyRoleSets || [],
                    companySummary: profileResponse.data.CompanySummary || null,
                    allWorkspaces: profileResponse.data.WorkspaceSummaries || [],
                    canManageCustomers: Boolean(profileResponse.data.CanManageCustomers),
                    isParentCompany: Boolean(profileResponse.data.IsParentCompany),
                    clientCompanyId
                }));
                return true;
            }
            else if (profileResponse.status === 404) {
                // User profile not found, logout the user
                console.log('User profile not found (404), logging out user');
                // Explicitly clear auth state so UI stops spinning
                setAuthState(prev => ({
                    ...prev,
                    isLoading: false,
                    isAuthenticated: false,
                    user: null,
                    userProfile: null,
                    clientCompanyId: undefined,
                    homeClientCompanyId: undefined,
                    userCompanyRoleSets: [],
                    allWorkspaces: [],
                    companySummary: null,
                    canManageCustomers: false,
                    isParentCompany: false,
                    error: 'User profile not found'
                }));
                // Route user to login immediately
                try {
                    location.route('/login');
                }
                catch (e) {
                    console.error('Failed to route to /login after 404 profile', e);
                }
                // Attempt Auth0 logout to clear session (safe even if it redirects afterwards)
                if (auth0Client) {
                    try {
                        auth0Client.logout({
                            logoutParams: {
                                returnTo: import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin,
                            },
                        });
                    }
                    catch (e) {
                        console.error('Error during Auth0 logout after 404 profile', e);
                    }
                }
                return false;
            }
        }
        catch (error) {
            console.error('Error fetching user profile:', error);
        }
        // If we get here, something went wrong with routing
        if (shouldRoute) {
            console.warn('Unable to determine user workspaces, redirecting to login');
            location.route('/login');
        }
        return false;
    };
    useEffect(() => {
        const initAuth0 = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const initialOrganization = urlParams.get('organization') || undefined;
                const auth0 = await createAuth0Client({
                    domain: import.meta.env.VITE_AUTH0_DOMAIN,
                    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
                    authorizationParams: {
                        redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin,
                        audience: import.meta.env.VITE_AUTH0_AUDIENCE, // Add audience for API access (may be omitted during invite)
                        scope: 'openid profile email offline_access', // Include offline_access for refresh tokens
                        ...(initialOrganization ? { organization: initialOrganization } : {}),
                    },
                    cacheLocation: 'localstorage', // Persist auth state across page refreshes
                    useRefreshTokens: true, // Enable refresh tokens for better UX
                });
                setAuth0Client(auth0);
                // Initialize API client with auth handlers
                const getToken = async () => {
                    try {
                        return await auth0.getTokenSilently();
                    }
                    catch (error) {
                        console.error('Error getting token for API client:', error);
                        return undefined;
                    }
                };
                apiClient.setAuthHandlers(getToken, () => {
                    // Soft logout: clear local Auth0 cache without a full-page redirect.
                    // ProtectedRoute will detect !isAuthenticated and navigate to /login.
                    auth0.logout({ openUrl: false });
                    setAuthState({
                        isLoading: false,
                        isAuthenticated: false,
                        user: null,
                        userProfile: null,
                        clientCompanyId: undefined,
                        homeClientCompanyId: undefined,
                        userCompanyRoleSets: [],
                        allWorkspaces: [],
                        companySummary: null,
                        canManageCustomers: false,
                        isParentCompany: false,
                    });
                });
                signalRService.init(getToken);
                // If Auth0 returned an error, surface it for troubleshooting
                if (window.location.search.includes('error=')) {
                    const params = new URLSearchParams(window.location.search);
                    const error = params.get('error');
                    const errorDescription = params.get('error_description');
                    console.error('Auth0 error callback:', { error, errorDescription });
                    // If user is already authenticated (e.g., invite consumed), proceed instead of blocking
                    try {
                        const isAuthed = await auth0.isAuthenticated();
                        const user = isAuthed ? await auth0.getUser() : undefined;
                        const clientCompanyId = getClientCompanyId(user);
                        if (isAuthed && user && clientCompanyId) {
                            await fetchUserProfileAndRoute(clientCompanyId, user, true);
                            return;
                        }
                    }
                    catch (e) {
                        // Swallow errors – query params parsing is best-effort
                        console.warn('Swallowed Auth0 isAuthenticated error during error callback processing', e);
                    }
                    setAuthState(prev => ({ ...prev, isLoading: false, error: errorDescription || error || 'Authentication error' }));
                    return;
                }
                // Check if we're handling a redirect callback
                if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                    try {
                        const { appState } = await auth0.handleRedirectCallback();
                        // Determine where to route after authentication completes.
                        // We check for a saved redirect URL first (from ProtectedRoute before login),
                        // then fall back to appState.returnTo (e.g., invitation links).
                        // IMPORTANT: We only capture the target URL here — routing and profile fetch
                        // happen below so auth state is always properly initialized.
                        let postLoginRoute = null;
                        const savedRedirectUrl = localStorage.getItem('orbitalx-redirect-after-login');
                        if (savedRedirectUrl) {
                            localStorage.removeItem('orbitalx-redirect-after-login');
                            try {
                                const parsed = new URL(savedRedirectUrl, window.location.origin);
                                const path = parsed.pathname || '/';
                                const isLoginPath = path === '/login' || path.startsWith('/login/');
                                if (!isLoginPath) {
                                    postLoginRoute = parsed.pathname + (parsed.search ? parsed.search : '');
                                }
                            }
                            catch {
                                // if parsing fails, fall through and skip
                            }
                        }
                        // If we have appState with a returnTo (e.g., invitation link), preserve it
                        // but avoid routing back to the login page to prevent a flicker while profile loads
                        // Narrow appState so we avoid `any` casts
                        if (!postLoginRoute) {
                            const typedState = (appState && typeof appState === 'object') ? appState : undefined;
                            if (typedState?.returnTo) {
                                const raw = String(typedState.returnTo);
                                const url = new URL(raw, window.location.origin);
                                const path = url.pathname || '/';
                                const isLoginPath = path === '/login' || path.startsWith('/login/');
                                if (!isLoginPath) {
                                    url.searchParams.delete('invitation');
                                    url.searchParams.delete('organization');
                                    url.searchParams.delete('organization_name');
                                    postLoginRoute = url.pathname + (url.search ? url.search : '');
                                }
                            }
                        }
                        // Get user info to determine default routing
                        const isAuthenticated = await auth0.isAuthenticated();
                        const user = isAuthenticated ? await auth0.getUser() : undefined;
                        const clientCompanyId = getClientCompanyId(user);
                        // If we have company info, fetch profile and set auth state
                        if (isAuthenticated && user && clientCompanyId) {
                            // Route to saved URL if we have one, otherwise let fetchUserProfileAndRoute handle it
                            if (postLoginRoute) {
                                location.route(postLoginRoute);
                                // Still fetch profile to populate auth state (don't let fetchUserProfileAndRoute re-route)
                                await fetchUserProfileAndRoute(clientCompanyId, user, false);
                            }
                            else {
                                await fetchUserProfileAndRoute(clientCompanyId, user, true);
                            }
                            return;
                        }
                        else {
                            // Not authenticated or missing company ID - redirect to login
                            console.warn('User not authenticated or missing company ID, redirecting to login');
                            location.route('/login');
                            return;
                        }
                    }
                    catch (error) {
                        console.error('Error handling redirect callback:', error);
                        setAuthState(prev => ({
                            ...prev,
                            error: 'Authentication failed',
                            isLoading: false,
                        }));
                        // Redirect to login page on error
                        location.route('/login');
                        return;
                    }
                }
                // Check if user is already authenticated (not a redirect callback)
                const isAuthenticated = await auth0.isAuthenticated();
                const user = isAuthenticated ? await auth0.getUser() : undefined;
                const clientCompanyId = getClientCompanyId(user);
                if (isAuthenticated && user && clientCompanyId) {
                    // Validate the token is actually usable before routing/fetching profile.
                    // Stale tokens from localStorage can make isAuthenticated() return true
                    // even when the session is expired (e.g. after deploys or long idle).
                    try {
                        await auth0.getTokenSilently();
                    }
                    catch {
                        // Token is stale — clear local session without a full-page redirect
                        await auth0.logout({ openUrl: false });
                        setAuthState({
                            isLoading: false,
                            isAuthenticated: false,
                            user: null,
                            userProfile: null,
                            clientCompanyId: undefined,
                            homeClientCompanyId: undefined,
                            userCompanyRoleSets: [],
                            allWorkspaces: [],
                            companySummary: null,
                            canManageCustomers: false,
                            isParentCompany: false,
                        });
                        return;
                    }
                    // Check if we're on root or login path - if so, we need to route to dashboard
                    const currentPath = window.location.pathname;
                    const shouldRoute = currentPath === '/' || currentPath === '/login';
                    // Fetch profile and set auth state (route if on root/login)
                    await fetchUserProfileAndRoute(clientCompanyId, user, shouldRoute);
                }
                else {
                    // Not authenticated - set auth state accordingly
                    setAuthState({
                        isLoading: false,
                        isAuthenticated: false,
                        user,
                        userProfile: undefined,
                        clientCompanyId,
                        homeClientCompanyId: undefined,
                        userCompanyRoleSets: [],
                        allWorkspaces: [],
                        canManageCustomers: false,
                        isParentCompany: false,
                    });
                }
            }
            catch (error) {
                console.error('Error initializing Auth0:', error);
                setAuthState({
                    isLoading: false,
                    isAuthenticated: false,
                    error: 'Failed to initialize authentication',
                    canManageCustomers: false,
                    isParentCompany: false,
                });
            }
        };
        initAuth0();
    }, []); // Only run once on mount - auth callback should be handled differently
    const login = useCallback(async (invitationParams) => {
        if (!auth0Client) {
            console.error('Auth0 client not initialized yet');
            return;
        }
        try {
            const authParams = {
                redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin,
                // For invitation acceptance, omit audience to avoid extra consent/flow interruptions
                ...(invitationParams?.invitation ? {} : { audience: import.meta.env.VITE_AUTH0_AUDIENCE }),
                scope: 'openid profile email offline_access',
            };
            // Add invitation parameters if provided
            if (invitationParams?.invitation) {
                authParams.invitation = invitationParams.invitation;
            }
            if (invitationParams?.organization) {
                authParams.organization = invitationParams.organization;
            }
            if (invitationParams?.login_hint) {
                authParams.login_hint = invitationParams.login_hint;
            }
            if (invitationParams?.prompt) {
                const pv = invitationParams.prompt;
                const allowed = ['none', 'login', 'consent', 'select_account'];
                if (allowed.includes(pv)) {
                    authParams.prompt = pv;
                }
            }
            await auth0Client.loginWithRedirect({
                authorizationParams: authParams,
                appState: {
                    // preserve invitation route context for post-login routing
                    returnTo: window.location.pathname + window.location.search,
                    organization: invitationParams?.organization || undefined,
                    organization_name: invitationParams?.organization_name || undefined,
                },
            });
        }
        catch (error) {
            console.error('Error during login:', error);
            setAuthState(prev => ({
                ...prev,
                error: 'Login failed',
            }));
        }
    }, [auth0Client]);
    const logout = async () => {
        if (!auth0Client)
            return;
        // Call server logout endpoint to clear cache (fire and forget)
        try {
            apiClient.post('/Users/Logout').catch(() => {
                // Silently handle errors - don't block logout
            });
        }
        catch {
            // Don't block logout on error
        }
        auth0Client.logout({
            logoutParams: {
                returnTo: import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin,
            },
        });
    };
    const getAccessToken = async () => {
        if (!auth0Client || !authState.isAuthenticated)
            return undefined;
        try {
            return await auth0Client.getTokenSilently();
        }
        catch (error) {
            console.error('Error getting access token:', error);
            return undefined;
        }
    };
    const refreshUserProfile = async () => {
        if (!authState.isAuthenticated || !authState.user || !authState.clientCompanyId || !auth0Client)
            return;
        try {
            await fetchUserProfileAndRoute(authState.clientCompanyId, authState.user, false);
        }
        catch (error) {
            console.error('Error refreshing user profile:', error);
        }
    };
    const contextValue = {
        ...authState,
        user: authState.user || null,
        userProfile: authState.userProfile || null,
        allWorkspaces: authState.allWorkspaces || null,
        error: authState.error || null,
        clientCompanyId: authState.clientCompanyId || null,
        homeClientCompanyId: authState.homeClientCompanyId || null,
        userCompanyRoleSets: authState.userCompanyRoleSets || null,
        isAuth0Ready: !!auth0Client,
        login,
        logout,
        getAccessToken,
        refreshUserProfile,
    };
    return (_jsx(AuthContext.Provider, { value: contextValue, children: children }));
};
